import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/" className="flex flex-col leading-none mb-10 text-center">
          <span className="font-sans font-black tracking-[0.14em] text-lg text-ink">PRESTIGE</span>
          <span className="font-serif italic text-sm tracking-[0.28em] text-faded">wellness co. — admin</span>
        </Link>

        <div className="rounded-2xl border border-tan bg-offwhite p-8">
          <div className="w-12 h-12 rounded-full bg-forest text-cream flex items-center justify-center mb-6">
            <Lock className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <h1 className="font-sans font-extrabold text-2xl tracking-tight text-ink">Admin sign in</h1>
          <p className="text-faded text-sm mt-1 mb-6">Enter your credentials to manage orders.</p>

          <form onSubmit={submit} className="space-y-4">
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              required
              className="w-full rounded-xl border border-tan bg-cream px-4 py-3 text-sm outline-none focus:border-forest transition-colors"
            />
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full rounded-xl border border-tan bg-cream px-4 py-3 text-sm outline-none focus:border-forest transition-colors"
            />
            {error && <p data-testid="admin-login-error" className="text-sm text-destructive">{error}</p>}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loading}
              className="group w-full flex items-center justify-center gap-3 rounded-full bg-forest text-cream px-6 py-3.5 text-sm font-bold tracking-widest uppercase transition-colors hover:bg-forest-deep disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={2} />
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
